// src/pages/Seller/Messages.jsx
import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Box, Button, List, ListItem,
  ListItemAvatar, ListItemText, Avatar, Chip, Divider,
  CircularProgress, Alert, TextField, InputAdornment,
} from '@mui/material';
import {
  Chat, Search, OpenInNew, Inventory,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { sellerAPI } from '../../services/api';
import { formatDate, getStatusColor } from '../../utils/helpers';
import toast from 'react-hot-toast';

const SellerMessages = () => {
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');

  useEffect(() => {
    const fetchEnquiries = async () => {
      try {
        const res  = await sellerAPI.getMyEnquiries();
        const data = Array.isArray(res.data) ? res.data : (res.data?.enquiries || res.data?.orders || []);
        setEnquiries(data);
      } catch {
        toast.error('Failed to load messages');
      } finally {
        setLoading(false);
      }
    };
    fetchEnquiries();
  }, []);

  const filtered = enquiries.filter(e =>
    !search ||
    (e.product || e.fruit?.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (e.buyerName || e.buyer?.company_name || '').toLowerCase().includes(search.toLowerCase()) ||
    String(e.id).includes(search)
  );

  if (loading) return (
    <Container sx={{ py: 4, textAlign: 'center' }}>
      <CircularProgress />
      <Typography sx={{ mt: 2 }}>Loading messages...</Typography>
    </Container>
  );

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>Messages</Typography>
          <Typography variant="body1" color="text.secondary">
            Communicate with buyers about their enquiries
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Inventory />} onClick={() => navigate('/seller/products')}>
          My Products
        </Button>
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <TextField fullWidth size="small"
          placeholder="Search by product, buyer, or enquiry ID..."
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
        />
      </Paper>

      {enquiries.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No enquiries yet. Buyers will contact you once they express interest in your products.
        </Alert>
      ) : filtered.length === 0 ? (
        <Alert severity="info">No enquiries match your search.</Alert>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <List disablePadding>
            {filtered.map((enquiry, index) => {
              const productName = enquiry.product || enquiry.productName || enquiry.fruit?.name || `Fruit #${enquiry.fruit_id}`;
              const buyerName   = enquiry.buyerName || enquiry.buyer?.company_name || enquiry.buyer?.email || 'Buyer';
              return (
                <React.Fragment key={enquiry.id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    alignItems="flex-start"
                    sx={{
                      py: 2.5, px: 3,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                    onClick={() => navigate(`/chat/${enquiry.id}`)}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'secondary.light', color: 'secondary.main', width: 48, height: 48 }}>
                        <Chat />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {productName}
                          </Typography>
                          <Chip
                            label={enquiry.status || 'pending'}
                            color={getStatusColor(enquiry.status)}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            From: <strong>{buyerName}</strong> · {enquiry.quantity} containers
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            Enquiry #{enquiry.id} · {formatDate(enquiry.updated_at || enquiry.created_at)}
                          </Typography>
                          {enquiry.message && (
                            <Typography variant="body2" color="text.primary" sx={{ mt: 0.5 }}>
                              "{enquiry.message.substring(0, 80)}{enquiry.message.length > 80 ? '...' : ''}"
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <Box sx={{ ml: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, pt: 0.5 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        endIcon={<OpenInNew />}
                        onClick={(e) => { e.stopPropagation(); navigate(`/chat/${enquiry.id}`); }}
                      >
                        Open Chat
                      </Button>
                    </Box>
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        </Paper>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
        {filtered.length} of {enquiries.length} enquiries
      </Typography>
    </Container>
  );
};

export default SellerMessages;
